import {
  computeSessionHealth,
  DEFAULT_SESSION_HEALTH_THRESHOLDS,
  recordSessionHealthFailure,
  trimSessionHealthFailures,
  type SessionHealthFailure,
  type SessionHealthInput,
  type SessionHealthVerdict,
} from "./chat-session-health.ts";

/**
 * Re-exported: `SessionHealthVerdict` is part of this module's public
 * surface — `SessionHealthHost.read()` and `poll()` return it — so consumers
 * and tests type their host stubs against the monitor instead of reaching
 * into the lower-level `chat-session-health` module.
 */
export type { SessionHealthVerdict } from "./chat-session-health.ts";

/**
 * Host-facing session-health monitor.
 *
 * JAISIU-4089 — the verdict computation in `chat-session-health.ts` is
 * pure; this module is the side-effecting shell that polls the host
 * on an interval and stamps the verdict onto it. The host exposes a
 * tiny surface (`SessionHealthHost`) that the monitor reads, so the
 * same module can be wired into a Lit `OpenClawApp` today and a React
 * or vanilla-DOM host tomorrow without changing the verdict logic.
 *
 * Lifecycle:
 *   - `startSessionHealthMonitor(host)` is called from the host's
 *     `firstUpdated` and returns a disposer that the host stores for
 *     `disconnectedCallback`.
 *   - The monitor runs at `POLL_INTERVAL_MS` and re-evaluates the
 *     verdict. On change it stamps `host.sessionHealthVerdict = …
 *     ` and the Lit render function re-paints the pill.
 *   - A `connect` event (websocket open) immediately triggers a fresh
 *     poll so the pill flips to "online" without waiting a full
 *     interval. A `disconnect` event flips it to "offline" on the
 *     next tick.
 *
 * Failures observed during the chat lifecycle (RPC errors, retries
 * that exhausted the budget) are appended via `noteSessionHealthFailure`,
 * which trims the buffer and re-runs the verdict on the same tick so
 * the operator sees the pill change immediately.
 */

import type { SessionHealth } from "./chat-session-health.ts";

export interface SessionHealthHost {
  connected: boolean;
  hello: unknown | null;
  pendingUserMessageIds?: readonly unknown[];
  /** Last error string the host is showing the operator. */
  lastError: string | null;
  /** Optional cap on the failure buffer. Defaults to 32. */
  maxFailureBuffer?: number;
  /** Optional thresholds. Defaults to `DEFAULT_SESSION_HEALTH_THRESHOLDS`. */
  thresholds?: typeof DEFAULT_SESSION_HEALTH_THRESHOLDS;
  /**
   * Stamp the verdict onto the host. The host should re-render
   * afterwards. Called synchronously from the monitor; cheap.
   */
  setSessionHealthVerdict(verdict: SessionHealthVerdict): void;
}

export interface SessionHealthMonitor {
  /** Read the current verdict without forcing a recompute. */
  read(): SessionHealthVerdict;
  /** Recompute on demand (e.g. after the host mutates `lastError`). */
  poll(): SessionHealthVerdict;
  /** Append a failure to the buffer and re-evaluate. */
  noteFailure(reason: string): SessionHealthVerdict;
  /** Force a "offline → online" check (called on websocket reconnect). */
  noteReconnect(): SessionHealthVerdict;
  /** Dispose the polling interval. */
  dispose(): void;
}

const POLL_INTERVAL_MS = 5_000;
const MAX_FAILURE_BUFFER_DEFAULT = 32;

/**
 * Start the monitor. Returns an object the host can use to record
 * failures and read the current verdict without waiting for a poll.
 */
export function startSessionHealthMonitor(host: SessionHealthHost): SessionHealthMonitor {
  const bufferCap = host.maxFailureBuffer ?? MAX_FAILURE_BUFFER_DEFAULT;
  let failures: SessionHealthFailure[] = [];
  let verdict: SessionHealthVerdict = computeSessionHealth(snapshot(), host.thresholds);
  // Stamp the initial verdict so the pill is correct on first paint.
  host.setSessionHealthVerdict(verdict);

  function snapshot(): SessionHealthInput {
    return {
      connected: host.connected,
      hasHello: host.hello != null,
      pendingOutboxCount: Array.isArray(host.pendingUserMessageIds)
        ? host.pendingUserMessageIds.length
        : 0,
      recentFailures: failures,
      nowMs: Date.now(),
    };
  }

  function recompute(): SessionHealthVerdict {
    const now = Date.now();
    failures = trimSessionHealthFailures(
      failures,
      now,
      host.thresholds?.recentFailureWindowMs ??
        DEFAULT_SESSION_HEALTH_THRESHOLDS.recentFailureWindowMs,
    );
    verdict = computeSessionHealth(snapshot(), host.thresholds);
    host.setSessionHealthVerdict(verdict);
    return verdict;
  }

  const interval = setInterval(() => {
    // Clear lastError-derived noise: if the host cleared its
    // `lastError`, do NOT clear the buffer — the buffer is the
    // smoothed health signal, not the toast. We only re-evaluate.
    void recompute();
  }, POLL_INTERVAL_MS);
  // Don't keep the node process alive purely for the health poll.
  if (typeof (interval as unknown as { unref?: () => void }).unref === "function") {
    (interval as unknown as { unref: () => void }).unref();
  }

  return {
    read: () => verdict,
    poll: recompute,
    noteFailure(reason: string) {
      const now = Date.now();
      failures = recordSessionHealthFailure(failures, { at: now, reason }, now);
      if (failures.length > bufferCap) {
        failures = failures.slice(failures.length - bufferCap);
      }
      return recompute();
    },
    noteReconnect() {
      // On reconnect, clear the failure buffer so a single old 429
      // does not keep the pill stuck on "degraded" for the next 30s.
      failures = [];
      return recompute();
    },
    dispose() {
      clearInterval(interval);
    },
  };
}

/**
 * Convenience: derive the current verdict kind from a host without
 * starting a monitor. Used by callers that only want the tri-state for
 * a one-off decision (e.g. disable the composer button).
 */
export function readSessionHealthKind(host: SessionHealthHost): SessionHealth {
  return computeSessionHealth(
    {
      connected: host.connected,
      hasHello: host.hello != null,
      pendingOutboxCount: Array.isArray(host.pendingUserMessageIds)
        ? host.pendingUserMessageIds.length
        : 0,
      recentFailures: [],
      nowMs: Date.now(),
    },
    host.thresholds,
  ).kind;
}
